import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { Sun, Moon } from 'lucide-react'
import { useColorMode } from '../../core/prefs/useColorMode'
import { useI18n } from '../../core/i18n/useI18n'
import { LANGUAGES } from '../../core/i18n/messages'
import { Flag } from '../components/Flag'
import { EASE_ORGANIC } from '../../styles/theme'

/** Segmentado Claro/Escuro com "thumb" deslizante. A troca dispara o reveal circular. */
function ThemeToggle() {
  const { mode, setMode } = useColorMode()
  const { t } = useI18n()
  const isDark = mode === 'dark'
  const options = [
    { value: 'light' as const, label: t('prefs.theme.light'), Icon: Sun },
    { value: 'dark' as const, label: t('prefs.theme.dark'), Icon: Moon },
  ]

  return (
    <Box
      role="group"
      aria-label={t('prefs.theme')}
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        p: '3px',
        width: 84,
        borderRadius: '999px',
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        userSelect: 'none',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '3px',
          bottom: '3px',
          left: '3px',
          width: 'calc(50% - 3px)',
          borderRadius: '999px',
          bgcolor: 'primary.main',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.20)',
          transform: isDark ? 'translateX(100%)' : 'translateX(0)',
          transition: `transform 300ms ${EASE_ORGANIC}`,
        }}
      />
      {options.map(({ value, label, Icon }) => {
        const selected = mode === value
        return (
          <ButtonBase
            key={value}
            onClick={(e) => setMode(value, { x: e.clientX, y: e.clientY })}
            aria-pressed={selected}
            aria-label={label}
            title={label}
            sx={{
              position: 'relative',
              zIndex: 1,
              py: '7px',
              borderRadius: '999px',
              color: selected ? 'primary.contrastText' : 'text.secondary',
              transition: 'color 220ms ease',
            }}
          >
            <Icon size={16} strokeWidth={2.4} />
          </ButtonBase>
        )
      })}
    </Box>
  )
}

/** Três bandeiras: a ativa ganha anel e opacidade cheia; as demais ficam esmaecidas. */
function LanguagePicker() {
  const { lang, setLang, t } = useI18n()
  return (
    <Box role="group" aria-label={t('prefs.language')} sx={{ display: 'inline-flex', gap: 0.75 }}>
      {LANGUAGES.map((l) => {
        const selected = lang === l.code
        return (
          <Tooltip key={l.code} title={l.label} enterDelay={400}>
            <ButtonBase
              onClick={() => setLang(l.code)}
              aria-pressed={selected}
              aria-label={l.label}
              sx={{
                p: '2px',
                borderRadius: '7px',
                outline: '2px solid',
                outlineColor: selected ? 'primary.main' : 'transparent',
                opacity: selected ? 1 : 0.5,
                transform: selected ? 'scale(1)' : 'scale(0.94)',
                transition: `opacity 200ms ${EASE_ORGANIC}, transform 200ms ${EASE_ORGANIC}`,
                '&:hover': { opacity: 1, transform: 'scale(1)' },
                '&.Mui-focusVisible': { outlineColor: 'primary.main' },
              }}
            >
              <Flag code={l.flag} size={24} />
            </ButtonBase>
          </Tooltip>
        )
      })}
    </Box>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {label}
      </Typography>
      {children}
    </Box>
  )
}

/** Bloco de preferências do menu da conta: tema (claro/escuro) e idioma (PT/EN/ES). */
export function AccountPreferences() {
  const { t } = useI18n()
  return (
    <Box sx={{ py: 0.5 }}>
      <Row label={t('prefs.theme')}>
        <ThemeToggle />
      </Row>
      <Row label={t('prefs.language')}>
        <LanguagePicker />
      </Row>
    </Box>
  )
}
